import { useState, useEffect, useCallback } from 'react';
import { getApiKey, removeCache } from '../../../utils/storage';
import { getAllOrganizationData } from '../../../utils/openai-api';
import { OrganizationData } from '../types';

export const useOrganizationData = () => {
  const [data, setData] = useState<OrganizationData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch data (with cache clear option)
  const fetchData = useCallback(async (clearCache = false) => {
    if (clearCache) {
      removeCache('organization_management_data');
    }

    const apiKey = getApiKey();
    if (!apiKey) {
      setError('Admin API Key is not configured');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await getAllOrganizationData(apiKey);

      if (!result.projects.success) {
        throw new Error(`Error fetching projects: ${result.projects.error}`);
      }

      if (!result.users.success) {
        throw new Error(`Error fetching users: ${result.users.error}`);
      }

      setData({
        projects: result.projects.data?.data || [],
        users: result.users.data?.data || [],
        projectDetails: result.projectDetails
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
      console.error('Data fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial data fetch
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, fetchData };
};
